import type { GridSortModel } from '@mui/x-data-grid-pro';
import type { SampleSearchCriteria } from '@/api/ApiClient';
import type { SampleRowType } from '@/components/tables/SampleGridCore';
import type { SampleListMode } from '@/lib/sampleList/sampleListMode';

/**
 * Default time-to-live for the in-memory sample grid snapshot (single tab).
 * When {@link getSampleGridSnapshotMaxAgeMs} is later wired to user settings,
 * keep this constant as the fallback when settings are absent.
 */
export const DEFAULT_SAMPLE_GRID_SNAPSHOT_MAX_AGE_MS = 30 * 60 * 1000;

/**
 * Returns the maximum age (ms) for a cached sample grid snapshot before it is treated as stale.
 * Today this returns {@link DEFAULT_SAMPLE_GRID_SNAPSHOT_MAX_AGE_MS}; later it can read
 * user preferences (e.g. minutes in UserSettings) without changing call sites.
 */
export function getSampleGridSnapshotMaxAgeMs(): number {
  return DEFAULT_SAMPLE_GRID_SNAPSHOT_MAX_AGE_MS;
}

type Snapshot = {
  token: string;
  cachedAt: number;
  rows: SampleRowType[];
  totalRows: number;
};

const snapshots: Record<SampleListMode, Snapshot | null> = {
  samples: null,
  substrates: null,
};

export type SampleGridSnapshotTokenInput = {
  listMode?: SampleListMode;
  filters: SampleSearchCriteria;
  paginationModel: { page: number; pageSize: number };
  sortModel: GridSortModel;
  /** Part of the cache key; incremented by `ensureFreshSampleGridServerRequest` on Apply so identical criteria still refetch. */
  applyGeneration: number;
};

/**
 * Builds a stable string key for the current grid request (filters + paging + sort + apply generation).
 */
export function buildSampleGridSnapshotToken(input: SampleGridSnapshotTokenInput): string {
  return JSON.stringify({
    listMode: input.listMode ?? 'samples',
    filters: input.filters,
    paginationModel: input.paginationModel,
    sortModel: input.sortModel,
    applyGeneration: input.applyGeneration,
  });
}

/**
 * Returns cached rows if the token matches, the snapshot is not older than {@link getSampleGridSnapshotMaxAgeMs},
 * and a snapshot exists.
 */
export function tryGetCachedSampleGridSnapshot(
  token: string,
  listMode: SampleListMode = 'samples'
): { rows: SampleRowType[]; totalRows: number } | null {
  const snapshot = snapshots[listMode];
  if (!snapshot) return null;
  if (snapshot.token !== token) return null;
  if (Date.now() - snapshot.cachedAt >= getSampleGridSnapshotMaxAgeMs()) return null;
  return { rows: snapshot.rows, totalRows: snapshot.totalRows };
}

/** Stores the latest successful search result for instant restore (e.g. back navigation). */
export function setCachedSampleGridSnapshot(
  token: string,
  rows: SampleRowType[],
  totalRows: number,
  listMode: SampleListMode = 'samples'
): void {
  snapshots[listMode] = {
    token,
    cachedAt: Date.now(),
    rows,
    totalRows,
  };
}

/** Clears snapshot(s) so the next load hits the server (mutations, Apply, etc.). */
export function invalidateSampleGridCache(listMode?: SampleListMode): void {
  if (listMode == null) {
    snapshots.samples = null;
    snapshots.substrates = null;
    return;
  }
  snapshots[listMode] = null;
}
